import type {
  BetslipHostEachWayRunnerPosition,
  BetslipHostPnl,
  BetslipHostRunnerPosition,
} from "@ppb/tbd-store/state/betslip/Betslip.types";
import type { UserDetails } from "@ppb/tbd-store/state/entities/user-details/UserDetailsState";
import type { PNLAndWhatIfProps, PnlEWRow } from "../components/PNLAndWhatIf/PNLAndWhatIf.types";
import { currencyFormatWithDecimalPlaces } from "../formatters/currency-formatters";

type CurrencyDetails = Pick<UserDetails, "localeCodeBcp47" | "currencyCode">;

export const hasPNLAndWhatIfValues = (
  pnlAndWhatIf: Pick<PNLAndWhatIfProps, "pnl" | "rawPnl" | "whatIf" | "rawWhatIf" | "pnlAndWhatIfEachWay"> | undefined,
): boolean =>
  (!!pnlAndWhatIf?.pnl && pnlAndWhatIf.rawPnl !== undefined) ||
  (!!pnlAndWhatIf?.whatIf && pnlAndWhatIf.rawWhatIf !== undefined) ||
  !!pnlAndWhatIf?.pnlAndWhatIfEachWay?.length;

// Only multi-winner markets keep lose apart from win; each way has its own rows.
export const shouldShowLose = (
  numberOfWinners: number | undefined,
  positionKind: BetslipHostRunnerPosition["kind"] | undefined,
): boolean => (numberOfWinners ?? 0) > 1 && positionKind === "GENERIC";

/**
 * Given the runner pnl and user details, build an object with formatted Pnl and raw Pnl values.
 */
export function buildPnl(
  pnl: BetslipHostPnl | undefined,
  userDetails: CurrencyDetails,
  showLose: boolean,
): { pnl?: string; rawPnl?: number; pnlLose?: string; rawPnlLose?: number } {
  const { win, lose } = pnl ?? {};

  if (typeof win === "undefined") {
    return {};
  }

  return {
    pnl: currencyFormatWithDecimalPlaces({ ...userDetails, value: win }),
    rawPnl: win,
    ...(showLose && typeof lose !== "undefined"
      ? { pnlLose: currencyFormatWithDecimalPlaces({ ...userDetails, value: lose }), rawPnlLose: lose }
      : {}),
  };
}

/**
 * Given the runner what-if and user details, build an object with formatted What-if and raw What-if values.
 */
export function buildWhatIf(
  whatIf: BetslipHostPnl | undefined,
  userDetails: CurrencyDetails,
  showLose: boolean,
): { whatIf?: string; rawWhatIf?: number; whatIfLose?: string; rawWhatIfLose?: number } {
  const { win, lose } = whatIf ?? {};

  if (typeof win === "undefined") {
    return {};
  }

  return {
    whatIf: currencyFormatWithDecimalPlaces({ ...userDetails, value: win }),
    rawWhatIf: win,
    ...(showLose && typeof lose !== "undefined"
      ? { whatIfLose: currencyFormatWithDecimalPlaces({ ...userDetails, value: lose }), rawWhatIfLose: lose }
      : {}),
  };
}

export function buildPnlAndWhatIfEachWay(
  userDetails: CurrencyDetails,
  runnerPosition: Pick<BetslipHostEachWayRunnerPosition, "pnl" | "potentialPnl" | "whatIf">,
): PnlEWRow[] | undefined {
  const { pnl, potentialPnl, whatIf } = runnerPosition;

  const { win: winRaw, placed: placedRaw, lose: unplacedRaw } = pnl;
  const { win: whatIfWinRaw, placed: whatIfPlacedRaw, lose: whatIfUnplacedRaw } = whatIf;
  const { win: potentialWinRaw, placed: potentialPlacedRaw, lose: potentialUnplacedRaw } = potentialPnl;

  const hasPnl = winRaw !== undefined && placedRaw !== undefined && unplacedRaw !== undefined;
  const hasPotential =
    potentialWinRaw !== undefined && potentialPlacedRaw !== undefined && potentialUnplacedRaw !== undefined;
  const hasWhatIf = whatIfWinRaw !== undefined && whatIfPlacedRaw !== undefined && whatIfUnplacedRaw !== undefined;

  let firstRow: PnlEWRow | undefined;
  let secondRow: PnlEWRow | undefined;

  const pnlAndWhatIfEachWay = [];

  if (hasPnl) {
    const win = currencyFormatWithDecimalPlaces({ ...userDetails, value: winRaw });
    const placed = currencyFormatWithDecimalPlaces({ ...userDetails, value: placedRaw });
    const unplaced = currencyFormatWithDecimalPlaces({ ...userDetails, value: unplacedRaw });

    firstRow = {
      hasMarker: !hasWhatIf,
      winRaw,
      placedRaw,
      unplacedRaw,
      win,
      placed,
      unplaced,
    };

    pnlAndWhatIfEachWay.push(firstRow);
  }

  if (!hasPnl && hasPotential) {
    const win = currencyFormatWithDecimalPlaces({ ...userDetails, value: potentialWinRaw });
    const placed = currencyFormatWithDecimalPlaces({ ...userDetails, value: potentialPlacedRaw });
    const unplaced = currencyFormatWithDecimalPlaces({ ...userDetails, value: potentialUnplacedRaw });

    firstRow = {
      hasMarker: true,
      winRaw: potentialWinRaw,
      placedRaw: potentialPlacedRaw,
      unplacedRaw: potentialUnplacedRaw,
      win,
      placed,
      unplaced,
    };

    pnlAndWhatIfEachWay.push(firstRow);
  }

  if (hasPnl && hasWhatIf) {
    const win = currencyFormatWithDecimalPlaces({ ...userDetails, value: whatIfWinRaw });
    const placed = currencyFormatWithDecimalPlaces({ ...userDetails, value: whatIfPlacedRaw });
    const unplaced = currencyFormatWithDecimalPlaces({ ...userDetails, value: whatIfUnplacedRaw });

    secondRow = {
      hasMarker: true,
      winRaw: whatIfWinRaw,
      placedRaw: whatIfPlacedRaw,
      unplacedRaw: whatIfUnplacedRaw,
      win,
      placed,
      unplaced,
    };

    pnlAndWhatIfEachWay.push(secondRow);
  }

  if (pnlAndWhatIfEachWay.length === 0) {
    return;
  }

  return pnlAndWhatIfEachWay;
}
